/**
 * Communication Template Service - SOP #1
 * Frontend service for communication template operations
 */

import api from "../../../shared/api.jsx";

/**
 * Generate communication template for a lead
 * @param {string} leadId - Lead ID
 * @param {string} templateType - Template type (welcome, photo_request, pre_quote_confirmation)
 * @returns {Promise<string>} Generated template text
 */
export const generateLeadTemplate = async (leadId, templateType) => {
  try {
    const response = await api.get(`/templates/lead/${leadId}/${templateType}`);
    return response.data.template;
  } catch (error) {
    console.error("Error generating lead template:", error);
    throw error;
  }
};

/**
 * Generate communication template for a quote
 * @param {string} quoteId - Quote ID
 * @param {string} templateType - Template type (quote_ready)
 * @returns {Promise<string>} Generated template text
 */
export const generateQuoteTemplate = async (quoteId, templateType) => {
  try {
    const response = await api.get(
      `/templates/quote/${quoteId}/${templateType}`
    );
    return response.data.template;
  } catch (error) {
    console.error("Error generating quote template:", error);
    throw error;
  }
};

/**
 * Generate public-safe lead summary for transporter platforms
 * @param {string} leadId - Lead ID
 * @returns {Promise<string>} Redacted public post text
 */
export const generateLeadPublicPostTemplate = async (leadId) => {
  try {
    const response = await api.get(`/templates/lead/${leadId}/public_post`);
    return response.data.template;
  } catch (error) {
    console.error("Error generating public post template:", error);
    throw error;
  }
};

const PUBLIC_POST_CLOSING =
  "\nReply with your rate and availability. Customer details shared only after assignment.";

/**
 * Remove the generic public-post closing line before appending a job-board CTA.
 * @param {string} template
 * @returns {string}
 */
export const stripPublicPostClosing = (template) => {
  if (!template) return "";
  if (template.endsWith(PUBLIC_POST_CLOSING)) {
    return template.slice(0, -PUBLIC_POST_CLOSING.length);
  }
  return template;
};

/**
 * Build shareable job-board messages from a public-safe lead summary.
 * @param {string} publicPostTemplate
 * @param {string} jobUrl
 * @returns {{ whatsapp: string, email: string, emailSubject: string, link: string }}
 */
export const buildJobBoardShareMessages = (publicPostTemplate, jobUrl) => {
  const body = stripPublicPostClosing(publicPostTemplate);
  const whatsapp = `${body}\n\n👉 *View job & submit your bid:*\n${jobUrl}`;
  const email = `${body}\n\nView job and submit your bid:\n${jobUrl}`;
  const subjectMatch = body.match(/Ref: (.+)$/m);
  const emailSubject = subjectMatch
    ? `Load available — Ref ${subjectMatch[1]}`
    : "Load available — Bubakii job board";

  return {
    whatsapp,
    email,
    emailSubject,
    link: jobUrl,
  };
};

/**
 * Copy text to clipboard
 * @param {string} text - Text to copy
 * @returns {Promise<boolean>} Success status
 */
export const copyToClipboard = async (text) => {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    } else {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      document.body.appendChild(textArea);
      textArea.select();
      const success = document.execCommand("copy");
      document.body.removeChild(textArea);
      return success;
    }
  } catch (error) {
    console.error("Error copying to clipboard:", error);
    return false;
  }
};
